/**
 * Play failure recovery extracted from QueueController (size ratchet / #578).
 */
import { useMaestroStore } from '../store/maestroStore';
import { showImperativeToast } from '../providers/UIProvider';
import { handleLocalPlayFailure } from '../utils/localPlayFailureUI';
import {
  markNetworkPlaySucceeded,
  markTransportPlayFailure,
} from '../utils/proxyFetchFailedUI';
import {
  showUserPlayRetryToast,
} from '../utils/playFailureUI';
import {
  FAILURE_SKIP_CAP,
  getLastObservedRendererTrack,
  verifyPlayUriFailureDelivery,
} from './playUriFailureVerify';
import {
  isPlayUriPending,
  logSkipConfirmedFailure,
  shouldRetainQueueTransportOwnership,
} from './playUriConfirmation';

export type PriorPlaybackSnapshot = {
  currentTrack: any;
  currentStreamUrl: string | null;
  currentStreamFormatId: number | null;
  alternateStreamUrl: string | null;
  alternateStreamFormatId: number | null;
  queueIndex: number;
  position: number;
  duration: number;
  playbackState: string;
  rendererHasStream: boolean;
};

type PlayIntent = 'user' | 'queue-advance';

export type QueuePlayFailureHost = {
  _playIntent: PlayIntent;
  _consecutiveSkips: number;
  _isPlayTrackInFlight: boolean;
  _inFlightTrackId: string | null;
  _claimFailureRecovery: (owner: string) => boolean;
  _releaseFailureRecovery: () => void;
  _restorePlaybackSnapshot: (snap: PriorPlaybackSnapshot | null) => void;
  _playTrack: (track: any, options?: { intent?: PlayIntent }) => Promise<void>;
  _clearPlayTrackCooldownForSkip: () => void;
};

export async function handleQueuePlayFailure(
  host: QueuePlayFailureHost,
  priorPlayback: PriorPlaybackSnapshot | null,
  options?: { suppressSkipToast?: boolean; errMsg?: string },
): Promise<void> {
  if (host._playIntent === 'user') {
    if (!host._claimFailureRecovery('user-pause')) return;
    const store = useMaestroStore.getState();
    const renderer = store.activeConnectorRenderer;
    const zoneName = renderer?.name || store.discovery?.activeZone?.name || 'your device';
    const trackTitle = store.currentTrack?.title || 'This track';
    showUserPlayRetryToast(
      trackTitle,
      zoneName,
      options?.errMsg,
      store.currentTrack,
      store.currentStreamFormatId,
      renderer?.id || store.discovery?.activeZone?.id,
    );
    markTransportPlayFailure(renderer?.id || store.discovery?.activeZone?.id);
    host._releaseFailureRecovery();
    return;
  }

  if (!host._claimFailureRecovery('queue-skip')) return;
  if (await trySkipOnPlayFailure(host, {
    suppressToast: options?.suppressSkipToast,
    errMsg: options?.errMsg,
  })) {
    return;
  }
  host._restorePlaybackSnapshot(priorPlayback);
  useMaestroStore.getState().setPlaybackState('paused');
  host._releaseFailureRecovery();
}

async function trySkipOnPlayFailure(
  host: QueuePlayFailureHost,
  options?: {
    suppressToast?: boolean;
    errMsg?: string;
  },
): Promise<boolean> {
  if (isPlayUriPending()) {
    console.warn('[QueueController] skip suppressed — PLAYING-PENDING');
    return false;
  }
  if (shouldRetainQueueTransportOwnership()) {
    console.warn('[QueueController] skip suppressed — queue ownership latched on acceptance evidence (#598 G)');
    return false;
  }

  const s = useMaestroStore.getState();
  const renderer = s.activeConnectorRenderer;
  const dispatchedUri = s.currentStreamUrl;
  const dispatchedTrack = s.queue[s.queueIndex]?.track || s.currentTrack;
  const failureReason = options?.errMsg || 'play_uri_failed';

  if (dispatchedUri && renderer?.id) {
    let verdict: 'late_success' | 'confirmed_failure' | 'inconclusive' = 'inconclusive';
    try {
      const C = await import('../lib/connector');
      verdict = await verifyPlayUriFailureDelivery({
        rendererId: renderer.id,
        rendererName: renderer.name,
        dispatchedUri,
        dispatchedTrack,
        failureReason,
        getTransportInfo: (id) => C.getTransportInfo(id),
        lastObserved: getLastObservedRendererTrack(renderer.id),
      });
    } catch {
      verdict = 'inconclusive';
    }

    if (verdict === 'late_success') {
      host._consecutiveSkips = 0;
      s.setPlaybackState('playing');
      s.setRendererHasStream(true);
      markNetworkPlaySucceeded(
        renderer.id || s.discovery?.activeZone?.id,
        renderer.name || s.discovery?.activeZone?.name,
      );
      console.warn(
        '[QueueController] playUri reported failure but renderer is playing the dispatched track — late success, no skip',
      );
      host._releaseFailureRecovery();
      return true;
    }

    if (verdict === 'inconclusive') {
      console.warn('[QueueController] playUri failure unverified — not skipping');
      return false;
    }
  }

  host._consecutiveSkips++;
  if (host._consecutiveSkips <= FAILURE_SKIP_CAP) {
    const failedTrack = s.queue[s.queueIndex]?.track;
    const nextIdx = s.queueIndex + 1;
    if (nextIdx < s.queue.length) {
      console.warn('[QueueController] Skipping failed track, attempt', host._consecutiveSkips);

      const trackTitle = failedTrack?.title || 'Track';
      const isLocal = failedTrack?.source === 'local_library';
      if (!options?.suppressToast) {
        if (isLocal) {
          handleLocalPlayFailure('generic', trackTitle);
        } else {
          showImperativeToast(
            `Skipped "${trackTitle}" — stream temporarily unavailable`,
            4000,
          );
        }
      }
      const rendererName =
        s.activeConnectorRenderer?.name || s.discovery?.activeZone?.name || 'renderer';
      logSkipConfirmedFailure('stream_unavailable', rendererName);

      s.setQueueIndex(nextIdx);
      const nextItem = s.queue[nextIdx];
      if (nextItem?.track) {
        host._isPlayTrackInFlight = false;
        host._inFlightTrackId = null;
        host._clearPlayTrackCooldownForSkip();
        host._releaseFailureRecovery();
        await host._playTrack(nextItem.track, { intent: 'queue-advance' });
        return true;
      }
    }
    host._consecutiveSkips = 0;
    return false;
  }

  const trackTitle = s.currentTrack?.title || s.queue[s.queueIndex]?.track?.title || 'This track';
  const named = String(failureReason).replace(/^\[object Object\]$/i, 'play failed');
  showImperativeToast(
    `Couldn't play "${trackTitle}" — ${named}. Stopped after ${FAILURE_SKIP_CAP} failed tracks.`,
    10000,
  );
  host._consecutiveSkips = 0;
  s.setPlaybackState('paused');
  host._releaseFailureRecovery();
  return true;
}
